import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, RotateCcw, Music2, Volume2, VolumeX } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const media = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

// ---- Procedural, mood-matched music bed (Web Audio) — no external files ----
function moodParams(mood) {
  const m = (mood || "").toLowerCase();
  if (/dark|tense|omin|synth|dread|sinister|villain|shadow/.test(m)) return { root: 110, minor: true, tempo: 300, wave: "sawtooth", arp: "triangle", gain: 0.06 };
  if (/epic|heroic|orchestr|brass|triumph|grand|soar|swell/.test(m)) return { root: 130.81, minor: false, tempo: 270, wave: "sawtooth", arp: "triangle", gain: 0.05 };
  if (/action|battle|chase|fast|intense|driv|pulse/.test(m)) return { root: 146.83, minor: true, tempo: 175, wave: "square", arp: "square", gain: 0.045 };
  if (/myster|ether|ambient|calm|dream|serene|melanch|wonder/.test(m)) return { root: 98, minor: true, tempo: 430, wave: "sine", arp: "sine", gain: 0.06 };
  return { root: 130.81, minor: false, tempo: 300, wave: "triangle", arp: "sine", gain: 0.05 };
}

function createMoodMusic(mood) {
  const p = moodParams(mood);
  const scale = p.minor ? [0, 3, 5, 7, 10, 12] : [0, 2, 4, 7, 9, 12];
  const nf = (semi) => p.root * Math.pow(2, semi / 12);
  let ctx = null, master = null, timer = null, nodes = [], muted = false, step = 0;
  const start = () => {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      master = ctx.createGain(); master.gain.value = muted ? 0 : p.gain; master.connect(ctx.destination);
      [0, 7].forEach((semi) => {
        const o = ctx.createOscillator(); o.type = p.wave; o.frequency.value = nf(semi - 12);
        const g = ctx.createGain(); g.gain.value = 0.5; o.connect(g); g.connect(master);
        const lfo = ctx.createOscillator(); lfo.frequency.value = 0.07;
        const lg = ctx.createGain(); lg.gain.value = 0.35; lfo.connect(lg); lg.connect(g.gain);
        lfo.start(); o.start(); nodes.push(o, lfo);
      });
      timer = setInterval(() => {
        if (!ctx) return;
        const semi = scale[step % scale.length] + ((Math.floor(step / scale.length) % 2) ? 12 : 0);
        const o = ctx.createOscillator(); o.type = p.arp; o.frequency.value = nf(semi);
        const g = ctx.createGain(); const now = ctx.currentTime;
        g.gain.setValueAtTime(0.0001, now);
        g.gain.exponentialRampToValueAtTime(0.5, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
        o.connect(g); g.connect(master); o.start(now); o.stop(now + 0.55);
        step += 1;
      }, p.tempo);
    } catch (e) { /* audio unavailable */ }
  };
  const stop = () => {
    try { if (timer) clearInterval(timer); timer = null; nodes.forEach((n) => { try { n.stop(); } catch (e) {} }); nodes = []; if (ctx) ctx.close(); ctx = null; } catch (e) {}
  };
  const setMuted = (mm) => { muted = mm; try { if (master) master.gain.value = mm ? 0 : p.gain; } catch (e) {} };
  return { start, stop, setMuted };
}

export function CinematicPlayer({ cinematic, autoplay = false }) {
  const shots = (cinematic?.shots || []).filter((s) => s.image_url);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const musicRef = useRef(null);

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} }
    if (musicRef.current) { musicRef.current.stop(); musicRef.current = null; }
    setPlaying(false);
  }, []);

  const start = useCallback(() => {
    if (!shots.length) return;
    setIdx(0);
    setPlaying(true);
    if (musicRef.current) musicRef.current.stop();
    musicRef.current = createMoodMusic(cinematic?.music_mood);
    musicRef.current.setMuted(muted);
    musicRef.current.start();
    const begin = (dur) => {
      const per = Math.max(2.6, (dur || shots.length * 4) / shots.length);
      let i = 0;
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        i += 1;
        if (i >= shots.length) { clearInterval(timerRef.current); }
        else setIdx(i);
      }, per * 1000);
    };
    const a = audioRef.current;
    if (a) {
      a.currentTime = 0;
      a.muted = muted;
      a.play().then(() => begin(a.duration || 0)).catch(() => begin(0));
    } else begin(0);
  }, [shots.length, muted, cinematic]);

  useEffect(() => {
    if (autoplay) start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEnded = () => { setPlaying(false); clearInterval(timerRef.current); setIdx(shots.length - 1); };

  if (!shots.length) {
    return <div className="aspect-video grid place-items-center rounded-2xl bg-black/60 text-white/60 text-sm" data-testid="cinematic-player-empty">No keyframes yet.</div>;
  }

  const cur = shots[idx] || shots[0];

  return (
    <div className="relative w-full aspect-video overflow-hidden rounded-2xl bg-black select-none" data-testid="cinematic-player">
      <style>{`@keyframes gvkb{0%{transform:scale(1) translate(0,0)}100%{transform:scale(1.14) translate(-2%,-2%)}}`}</style>
      {shots.map((s, i) => (
        <img
          key={i}
          src={media(s.image_url)}
          alt={s.caption || `shot ${i + 1}`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: i === idx ? 1 : 0, animation: i === idx && playing ? "gvkb 6s ease-out forwards" : "none" }}
          data-testid={`cinematic-shot-${i}`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/40" />

      {/* Title / tagline intro */}
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
        <div>
          <h3 className="font-display text-xl sm:text-2xl font-black text-white drop-shadow" data-testid="cinematic-title">{cinematic.title}</h3>
          {cinematic.tagline && <p className="text-xs sm:text-sm text-white/80 mt-0.5 max-w-md">{cinematic.tagline}</p>}
        </div>
        {cinematic.music_mood && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-black/50 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-white/90"><Music2 className="w-3 h-3" /> {cinematic.music_mood}</span>
        )}
      </div>

      {/* Caption */}
      {cur.caption && (
        <div className="absolute bottom-16 left-0 right-0 px-6 text-center">
          <p key={idx} className="inline-block font-display text-lg sm:text-2xl font-bold text-white drop-shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="cinematic-caption">{cur.caption}</p>
        </div>
      )}

      {/* Progress dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        {shots.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40"}`} />)}
      </div>

      {/* Controls */}
      <div className="absolute bottom-4 left-4 flex items-center gap-2">
        <button onClick={playing ? stop : start} className="w-10 h-10 grid place-items-center rounded-full bg-white text-black hover:scale-105 transition-transform" data-testid="cinematic-play-toggle">
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <button onClick={start} className="w-9 h-9 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25" title="Restart" data-testid="cinematic-restart"><RotateCcw className="w-4 h-4" /></button>
        <button onClick={() => { setMuted((m) => { const nm = !m; if (audioRef.current) audioRef.current.muted = nm; if (musicRef.current) musicRef.current.setMuted(nm); return nm; }); }} className="w-9 h-9 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25" title="Mute" data-testid="cinematic-mute">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
      </div>

      {/* Big play overlay when idle — backdrop lets clicks pass to the transport controls */}
      {!playing && (
        <div className="absolute inset-0 grid place-items-center bg-black/25 pointer-events-none">
          <button onClick={start} className="pointer-events-auto w-20 h-20 grid place-items-center rounded-full bg-white/90 text-black hover:scale-110 transition-transform shadow-2xl" data-testid="cinematic-big-play"><Play className="w-9 h-9 ml-1" /></button>
        </div>
      )}

      {cinematic.voice_url && <audio ref={audioRef} src={media(cinematic.voice_url)} onEnded={onEnded} preload="auto" />}
    </div>
  );
}

export default CinematicPlayer;
